import moment from 'moment-timezone';
import { maybePluralize } from '@nuso/common/util-string';

/**
 * Check if a Date object is valid (not Invalid Date).
 */
export function isValidDate(d: Date): boolean {
  return d instanceof Date && !isNaN(d.getTime());
}

export function isHolidayOrWeekend(d: Date, tz?: string): boolean {
  let day: number, month: number, date: number;
  if (tz) {
    const m = moment.tz(d, tz);
    day = m.day();
    month = m.month();
    date = m.date();
  } else {
    day = d.getDay();
    month = d.getMonth();
    date = d.getDate();
  }
  return (
      // Saturday or Sunday
      day === 0 || day === 6 ||
      // 4th of July
      (month === 6 && date === 4) ||
      // Christmas eve
      (month === 11 && date === 24) ||
      // Christmas day
      (month === 11 && date === 25) ||
      // New Year's day
      (month === 0 && date === 1) ||
      // Labor day (1st Mon in Sept)
      (month === 8 && date <= 7 && day === 1) ||
      // Memorial day (last Mon in May)
      (month === 4 && date >= 25 && day === 1) ||
      // Thanksgiving (4th Thurs in Nov)
      (month === 10 && date >= 22 && date <= 28 && day === 4) ||
      // day after Thanksgiving (4th Fri in Nov)
      (month === 10 && date >= 23 && date <= 29 && day === 5)
  );
}

function futureDateIsNotHoliday(d: Date, numberOfDays: number) {
  const futureDate = new Date(+d + ((86400000 * numberOfDays)));
  return !isHolidayOrWeekend(futureDate) ? futureDate : null;
}

export function workingDayBeforeDate(d: Date): Date {
  const dateBefore = new Date(+d - ((86400000 * 1)));
  dateBefore.setHours(0);
  dateBefore.setMinutes(0);
  dateBefore.setSeconds(0);

  return !isHolidayOrWeekend(dateBefore) ? dateBefore : workingDaysBeforeDate(dateBefore);
}

export function workingDaysBeforeDate(d: Date, numberOfDaysBefore: number = 1): Date {
  if (numberOfDaysBefore < 1) { throw new Error('negative days before NOT supported for workingDaysBeforeDate') }

  const workingDateBefore = workingDayBeforeDate(d);
  return numberOfDaysBefore === 1 ? workingDateBefore : workingDaysBeforeDate(workingDateBefore, numberOfDaysBefore - 1);
}

export function businessDaysFromNowInTz(n: number, tz: string): Date {
  if (n < 1) { throw new Error('n must be >= 1'); }
  const m = moment.tz(tz).startOf('day');
  let added = 0;
  while (added < n) {
    m.add(1, 'day');
    if (!isHolidayOrWeekend(m.toDate(), tz)) { added++; }
  }
  return m.toDate();
}

export function getLastPortDate() {
  const today = new Date();
  let nextDate = futureDateIsNotHoliday(today, 30);
  if (nextDate) { return nextDate; }
  nextDate = futureDateIsNotHoliday(today, 29);
  if (nextDate) { return nextDate; }
  nextDate = futureDateIsNotHoliday(today, 28);
  if (nextDate) { return nextDate; }
  nextDate = futureDateIsNotHoliday(today, 27);
  if (nextDate) { return nextDate; }
  nextDate = futureDateIsNotHoliday(today, 26);
  if (nextDate) { return nextDate; }
  return null;
}

export function isDuringPortingHours(requestedFocDate: Date, source = 'BANDWIDTH') {
  const centralDate = moment(requestedFocDate).tz('US/Central');
  switch(source) {
    case 'BRIGHTLINK':
      if ( (centralDate.hours() >= 7 && centralDate.hours() <= 16) || (centralDate.hours() === 17 && centralDate.minutes() === 0) ) {
        return true;
      } else {
        return false;
      }
    case 'BANDWIDTH':
    default:
      if ( centralDate.hours() >= 7 && (centralDate.hours() <= 18 || (centralDate.hours() === 19 && centralDate.minutes() === 0))  ) {
        return true;
      } else {
        return false;
      }
  }
}

export function dayOfWeek(d: Date): string {
  if (!d) { return ''; }
  const dayIndex = d.getDay();

  switch (dayIndex) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      return 'Invalid';
  }
}

export function mmddyyyyDate(d: Date | null) {
  return !d ? '' : `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
}

export function isBetweenDates(dateToCheck: Date | string | number, startDate: Date | string | number, endDate: Date | string | number) {
  if (!dateToCheck || !startDate || !endDate) { return false; }

  const d = new Date(dateToCheck);
  const s = new Date(startDate);
  const e = new Date(endDate);
  d.setHours(0, 0, 0, 0);
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return (+s <= +d && +d <= +e);
}

type DateLike = Date | string | number | null | undefined;

export function datesAndTimesMatch(date1: DateLike, date2: DateLike): boolean {
  if (!date1 || !date2) { return false; }

  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return +d1 === +d2;
}

export function isDateMatch(date1: Date | string | number, date2: Date | string | number) {
  if (!date1 || !date2) { return false; }

  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  console.log({
    d1,
    d2
  })
  return datesAndTimesMatch(d1, d2);
}

export function toUnixEpoch(d: Date): number | null {
  if (!d) {
    return null;
  }

  return Math.floor(d.getTime() / 1000);
}

export function currentUnixEpoch() {
  return toUnixEpoch(new Date());
}

export function unixEpochToDate(unixEpoch: number | null) {
  return unixEpoch ? new Date(unixEpoch * 1000) : null;
}

// > d = new Date()
// 2022-02-17T19:32:30.196Z
// > d.toJSON().replace(/(:)/g, '.',).slice(0,19)
// '2022-02-17T19.32.30'
export function dateTimeToFilename(d: Date) {
  return d.toJSON().replace(/(:)/g, '.',).slice(0,19);
}

export function convertAtlDateForDisplay(atlDate: Date) {
  const d = new Date(atlDate);
  const year = d.getUTCFullYear();
  const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  const displayDate = new Date(`${year}-${month}-${day} 12:00 AM`);
  return displayDate;
}

export function dateToHHMMSSmmm(d: Date) {
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const milliseconds = String(d.getMilliseconds()).padStart(3, '0');

  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

export function getTimeString(givenSeconds: number): string {
  const hours = Math.floor(givenSeconds / 3600);
  const minutes = Math.floor((givenSeconds - (hours * 3600)) / 60);
  const seconds = givenSeconds - (hours * 3600) - (minutes * 60);
  return [
    hours ? `${hours.toString()} ${maybePluralize('hour', hours)}` : '',
    minutes ? `${minutes.toString()} ${maybePluralize('minute', minutes)}` : '',
    seconds ? `${seconds.toString()} ${maybePluralize('second', seconds)}` : '',
  ].join(' ');
}

export function getTimezoneOffset(timezone: string | undefined, date?: Date | string | number): string {
  const safeTimezone = timezone || 'UTC';
  const targetDate = date || new Date();
  const offsetMinutes = moment.tz(targetDate, safeTimezone).utcOffset(); // Offset in minutes
  const hours = Math.floor(Math.abs(offsetMinutes) / 60)
    .toString()
    .padStart(2, '0');
  const minutes = Math.abs(offsetMinutes % 60)
    .toString()
    .padStart(2, '0');
  const sign = offsetMinutes >= 0 ? '+' : '-';
  return `${sign}${hours}${minutes}`;
}

export function formatUTCDateString(d: Date) {
  const year = d.getUTCFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const monthStr = month < 10 ? `0${month}` : month;
  const dayStr = day < 10 ? `0${day}` : day;
  return `${year}-${monthStr}-${dayStr}`;
}

